import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import StatusBadge from '../components/StatusBadge';
import Pagination from '../components/Pagination';

function formatTs(ts: string | null) {
  if (!ts) return '-';
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
    + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

function formatTsFull(ts: string | null) {
  if (!ts) return '-';
  return new Date(ts).toLocaleString();
}

function formatDuration(ms: number | null) {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function computeDuration(start: string | null, end: string | null): number | null {
  if (!start || !end) return null;
  return new Date(end).getTime() - new Date(start).getTime();
}

function JobDetailModal({ jobId, onClose }: { jobId: number; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRequestIds, setExpandedRequestIds] = useState<number[]>([]);

  useEffect(() => {
    setExpandedRequestIds([]);
    setData(null);
    setError('');
    setLoading(true);
    api.getJobDetail(jobId)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [jobId]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const toggleRequest = (requestId: number) => {
    setExpandedRequestIds(ids => (
      ids.includes(requestId)
        ? ids.filter(id => id !== requestId)
        : [...ids, requestId]
    ));
  };

  const formatPayload = (value: unknown) => JSON.stringify(value, null, 2);

  const job = data?.job;
  const queueWaitMs = job ? computeDuration(job.enqueued_at, job.started_at) : null;
  const runDurationMs = job?.duration_ms ?? computeDuration(job?.started_at, job?.completed_at);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>Job #{jobId}</h2>
          <button onClick={onClose} style={closeBtn}>&times;</button>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : error ? (
          <div style={{ color: '#ef4444' }}>{error}</div>
        ) : data ? (
          <>
            {/* Job summary cards */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <div style={metaCard}>
                <div style={metaLabel}>Target</div>
                <div style={metaValue}>{job.display_name || job.slug}</div>
              </div>
              <div style={metaCard}>
                <div style={metaLabel}>Platform</div>
                <div style={metaValue}>{job.platform_id}</div>
              </div>
              <div style={metaCard}>
                <div style={metaLabel}>Status</div>
                <div><StatusBadge status={job.status} /></div>
              </div>
              <div style={metaCard}>
                <div style={metaLabel}>Trigger</div>
                <div style={metaValue}>{job.trigger || '-'}</div>
              </div>
            </div>

            {/* Timing row */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <div style={metaCard}>
                <div style={metaLabel}>Enqueued</div>
                <div style={{ ...metaValue, fontSize: '12px', fontFamily: 'monospace' }}>{formatTsFull(job.enqueued_at)}</div>
              </div>
              <div style={metaCard}>
                <div style={metaLabel}>Started</div>
                <div style={{ ...metaValue, fontSize: '12px', fontFamily: 'monospace' }}>{formatTsFull(job.started_at)}</div>
              </div>
              <div style={metaCard}>
                <div style={metaLabel}>Completed</div>
                <div style={{ ...metaValue, fontSize: '12px', fontFamily: 'monospace' }}>{formatTsFull(job.completed_at)}</div>
              </div>
              <div style={metaCard}>
                <div style={metaLabel}>Queue Wait</div>
                <div style={{ ...metaValue, color: queueWaitMs && queueWaitMs > 30000 ? '#ef4444' : undefined }}>
                  {formatDuration(queueWaitMs)}
                </div>
              </div>
              <div style={metaCard}>
                <div style={metaLabel}>Run Time</div>
                <div style={metaValue}>{formatDuration(runDurationMs)}</div>
              </div>
              {job.worker_ip && (
                <div style={metaCard}>
                  <div style={metaLabel}>Worker</div>
                  <div style={{ ...metaValue, fontSize: '12px', fontFamily: 'monospace' }}>{job.worker_ip}</div>
                </div>
              )}
            </div>

            {/* Request stats bar */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', fontSize: '13px' }}>
              <span style={{ color: 'var(--gray-500)' }}>{data.summary.total} requests</span>
              <span style={{ color: '#22c55e' }}>{data.summary.succeeded} succeeded</span>
              {data.summary.failed > 0 && <span style={{ color: '#ef4444' }}>{data.summary.failed} failed</span>}
              {data.summary.pending > 0 && <span style={{ color: 'var(--gray-400)' }}>{data.summary.pending} pending</span>}
              {data.summary.processing > 0 && <span style={{ color: '#3b82f6' }}>{data.summary.processing} processing</span>}
            </div>

            {job.error && (
              <div style={{ padding: '8px 12px', marginBottom: '16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#ef4444', fontSize: '12px', fontFamily: 'monospace' }}>
                {job.error}
              </div>
            )}

            {/* Requests table */}
            <div style={{ overflowX: 'auto', maxHeight: '55vh', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left', position: 'sticky', top: 0, background: '#fff' }}>
                    <th style={rth}></th>
                    <th style={rth}>#</th>
                    <th style={rth}>Endpoint</th>
                    <th style={rth}>Status</th>
                    <th style={rth}>Started</th>
                    <th style={rth}>Duration</th>
                    <th style={rth}>Size</th>
                    <th style={rth}>Tries</th>
                    <th style={rth}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.map((r: any) => {
                    const expanded = expandedRequestIds.includes(r.id);
                    const requestPayload = r.request_json || {
                      method: r.method,
                      path: r.path,
                      params: r.params
                    };
                    const responsePayload = r.response_json ?? (r.error ? { error: r.error } : null);

                    return (
                      <React.Fragment key={r.id}>
                        <tr style={{
                          borderBottom: expanded ? '0' : '1px solid var(--gray-100)',
                          background: r.status === 'failed' ? '#fef2f2' : r.status === 'pending' ? '#fafafa' : undefined
                        }}>
                          <td style={rtd}>
                            <button type="button" onClick={() => toggleRequest(r.id)} style={inspectBtn}>
                              {expanded ? 'Hide' : 'View'}
                            </button>
                          </td>
                          <td style={{ ...rtd, color: 'var(--gray-400)' }}>{r.sequence_number}</td>
                          <td style={{ ...rtd, fontFamily: 'monospace', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={r.endpoint_key}>
                            {r.endpoint_key}
                            {r.required === false && <span style={{ marginLeft: '4px', fontSize: '10px', color: 'var(--gray-400)' }}>(opt)</span>}
                          </td>
                          <td style={rtd}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: r.status === 'succeeded' ? '#dcfce7' : r.status === 'failed' ? '#fee2e2' : r.status === 'processing' ? '#dbeafe' : '#f3f4f6',
                              color: r.status === 'succeeded' ? '#166534' : r.status === 'failed' ? '#991b1b' : r.status === 'processing' ? '#1e40af' : '#6b7280',
                            }}>
                              {r.status}
                            </span>
                          </td>
                          <td style={{ ...rtd, fontFamily: 'monospace', fontSize: '11px', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                            {formatTs(r.started_at)}
                          </td>
                          <td style={{ ...rtd, fontVariantNumeric: 'tabular-nums', color: 'var(--gray-500)' }}>
                            {r.duration_ms != null ? `${r.duration_ms}ms` : '-'}
                          </td>
                          <td style={{ ...rtd, fontVariantNumeric: 'tabular-nums', color: 'var(--gray-500)' }}>
                            {r.response_size ? `${(r.response_size / 1024).toFixed(1)}KB` : '-'}
                          </td>
                          <td style={{ ...rtd, fontVariantNumeric: 'tabular-nums', color: r.attempt_count > 1 ? '#f59e0b' : 'var(--gray-400)' }}>
                            {r.attempt_count ?? '-'}
                          </td>
                          <td style={{ ...rtd, color: '#ef4444', fontFamily: 'monospace', fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={r.error || ''}>
                            {r.error || ''}
                          </td>
                        </tr>
                        {expanded && (
                          <tr style={{ borderBottom: '1px solid var(--gray-100)', background: '#f8fafc' }}>
                            <td colSpan={9} style={{ padding: '0 10px 12px' }}>
                              <div style={payloadMetaBar}>
                                <span><strong>Path:</strong> <code>{r.path}</code></span>
                                <span><strong>Method:</strong> <code>{r.method}</code></span>
                                <span><strong>Template:</strong> <code>{r.endpoint_template_key}</code></span>
                                {r.required != null && <span><strong>Required:</strong> {r.required ? 'Yes' : 'No'}</span>}
                              </div>
                              <div style={{ ...payloadMetaBar, borderTop: '1px solid var(--gray-100)', paddingTop: '8px', marginTop: 0 }}>
                                <span><strong>Enqueued:</strong> {formatTsFull(r.enqueued_at)}</span>
                                <span><strong>Started:</strong> {formatTsFull(r.started_at)}</span>
                                <span><strong>Completed:</strong> {formatTsFull(r.completed_at)}</span>
                                {r.attempt_count > 1 && <span style={{ color: '#f59e0b' }}><strong>Attempts:</strong> {r.attempt_count}</span>}
                              </div>
                              <div style={payloadGrid}>
                                <div style={payloadCard}>
                                  <div style={payloadLabel}>Request</div>
                                  <pre style={payloadPre}>{formatPayload(requestPayload)}</pre>
                                </div>
                                <div style={payloadCard}>
                                  <div style={payloadLabel}>Response</div>
                                  <pre style={payloadPre}>
                                    {responsePayload ? formatPayload(responsePayload) : 'No response payload saved.'}
                                  </pre>
                                </div>
                              </div>
                              {r.meta && Object.keys(r.meta).length > 0 && (
                                <div style={{ ...payloadCard, marginTop: '12px' }}>
                                  <div style={payloadLabel}>Meta</div>
                                  <pre style={{ ...payloadPre, maxHeight: '120px' }}>{formatPayload(r.meta)}</pre>
                                </div>
                              )}
                              {Array.isArray(r.drift_alerts) && r.drift_alerts.length > 0 && (
                                <div style={driftCard}>
                                  <div style={payloadLabel}>Drift Alerts</div>
                                  <pre style={{ ...payloadPre, maxHeight: '180px' }}>{formatPayload(r.drift_alerts)}</pre>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function JobsScreen() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [platform, setPlatform] = useState('');
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getJobs({ page, limit: 50, status, platform });
      setData(result);
    } finally {
      setLoading(false);
    }
  }, [page, status, platform]);

  useEffect(() => {
    api.getPlatforms().then(r => setPlatforms(r.platforms || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleFilterChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setter(e.target.value);
    setPage(1);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: 'var(--gray-900)' }}>Sync Jobs</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: '14px' }}>Recent scrape job history</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { setLoading(true); fetchJobs(); }} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={platform} onChange={handleFilterChange(setPlatform)} style={selectStyle}>
          <option value="">All Platforms</option>
          {platforms.map((p: any) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
        </select>
        <select value={status} onChange={handleFilterChange(setStatus)} style={selectStyle}>
          <option value="">All Status</option>
          <option value="succeeded">Succeeded</option>
          <option value="failed">Failed</option>
          <option value="processing">Processing</option>
          <option value="queued">Queued</option>
        </select>
      </div>

      {loading && !data ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : data ? (
        <div className="card">
          <Pagination page={data.pagination.page} pages={data.pagination.pages} total={data.pagination.total} onPageChange={setPage} />
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
                  <th style={th}>ID</th>
                  <th style={th}>Platform</th>
                  <th style={th}>Target</th>
                  <th style={th}>Status</th>
                  <th style={th}>Started</th>
                  <th style={th}>Run Time</th>
                  <th style={th}>Queue Wait</th>
                  <th style={th}>Calls</th>
                  <th style={th}>Size</th>
                  <th style={th}>Error</th>
                </tr>
              </thead>
              <tbody>
                {data.jobs.map((j: any) => {
                  const queueWait = computeDuration(j.enqueued_at, j.started_at);
                  const runTime = j.duration_ms ?? computeDuration(j.started_at, j.completed_at);
                  return (
                    <tr key={j.id}
                      style={{ borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}
                      onClick={() => setSelectedJobId(j.id)}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--gray-50, #f9fafb)')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}
                    >
                      <td style={{ ...td, color: 'var(--gray-400)', fontSize: '12px' }}>#{j.id}</td>
                      <td style={td}>{j.platform_id}</td>
                      <td style={{ ...td, fontWeight: 500 }}>{j.display_name || j.slug}</td>
                      <td style={td}><StatusBadge status={j.status} /></td>
                      <td style={{ ...td, color: 'var(--gray-500)', fontSize: '12px', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                        {j.started_at ? formatTsFull(j.started_at) : (j.enqueued_at ? formatTsFull(j.enqueued_at) : '-')}
                      </td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: 'var(--gray-500)' }}>
                        {formatDuration(runTime)}
                      </td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: queueWait && queueWait > 30000 ? '#ef4444' : 'var(--gray-400)', fontSize: '12px' }}>
                        {formatDuration(queueWait)}
                      </td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                        {j.requests_succeeded != null
                          ? <><span style={{ color: '#22c55e' }}>{j.requests_succeeded}</span><span style={{ color: 'var(--gray-400)' }}> / </span><span style={{ color: j.requests_failed > 0 ? '#ef4444' : 'var(--gray-400)' }}>{j.requests_failed}</span></>
                          : (j.calls_count || '-')
                        }
                      </td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: 'var(--gray-500)', fontSize: '12px' }}>
                        {j.raw_bytes ? `${(j.raw_bytes / 1024).toFixed(1)}KB` : '-'}
                      </td>
                      <td style={{ ...td, color: '#ef4444', fontFamily: 'monospace', fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {j.error || ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={data.pagination.page} pages={data.pagination.pages} total={data.pagination.total} onPageChange={setPage} />
        </div>
      ) : null}

      {selectedJobId && (
        <JobDetailModal jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />
      )}
    </>
  );
}

const th: React.CSSProperties = { padding: '8px 12px' };
const td: React.CSSProperties = { padding: '8px 12px' };
const rth: React.CSSProperties = { padding: '6px 10px' };
const rtd: React.CSSProperties = { padding: '6px 10px' };
const inspectBtn: React.CSSProperties = {
  border: '1px solid var(--gray-200)',
  background: '#fff',
  color: 'var(--gray-700)',
  borderRadius: '999px',
  padding: '3px 8px',
  fontSize: '11px',
  fontWeight: 600,
  cursor: 'pointer',
};
const selectStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid var(--gray-300)',
  fontSize: '13px',
  background: '#fff',
  cursor: 'pointer',
};

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modal: React.CSSProperties = {
  background: '#fff',
  borderRadius: '12px',
  padding: '24px',
  width: '90vw',
  maxWidth: '1060px',
  maxHeight: '85vh',
  overflow: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
  color: 'var(--gray-400)',
  padding: '4px 8px',
};

const metaCard: React.CSSProperties = {
  padding: '8px 14px',
  background: 'var(--gray-50, #f9fafb)',
  borderRadius: '8px',
  minWidth: '80px',
};

const metaLabel: React.CSSProperties = {
  fontSize: '11px',
  color: 'var(--gray-500)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '2px',
};

const metaValue: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
};

const payloadMetaBar: React.CSSProperties = {
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  padding: '10px 0',
  color: 'var(--gray-600)',
  fontSize: '12px',
};

const payloadGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '12px',
};

const payloadCard: React.CSSProperties = {
  border: '1px solid var(--gray-200)',
  borderRadius: '10px',
  background: '#fff',
  overflow: 'hidden',
};

const payloadLabel: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--gray-200)',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--gray-500)',
};

const payloadPre: React.CSSProperties = {
  margin: 0,
  padding: '10px',
  maxHeight: '320px',
  overflow: 'auto',
  fontSize: '11px',
  lineHeight: 1.5,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  background: '#f8fafc',
  color: 'var(--gray-800)',
};

const driftCard: React.CSSProperties = {
  ...payloadCard,
  marginTop: '12px',
  borderColor: '#fde68a',
};
